import 'react-datepicker/dist/react-datepicker.css';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'react-daisyui';
import DatePicker from 'react-datepicker';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { getProviderCapabilities } from '../entities/provider';
import { DashboardHeaderProvider, useDashboardHeaderSlot } from '../shared/contexts/DashboardHeader.context';
import { DateRangeProvider } from '../shared/contexts/DateRange.provider';
import { useProjects, useProjectsMeta } from '../shared/contexts/Projects.provider';

const today = new Date();

const dateRanges = [
  { id: 1, label: 'Last 15 Days', from: new Date(today.getTime() - 15 * 24 * 60 * 60 * 1000), to: today },
  { id: 2, label: 'Last 30 Days', from: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), to: today },
  {
    id: 3,
    label: 'Last 60 Days',
    from: new Date(today.getTime() - 60 * 24 * 60 * 60 * 1000),
    to: today,
    default: true,
  },
  { id: 4, label: 'Last 90 Days', from: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), to: today },
  {
    id: 5,
    label: 'Last 180 Days',
    from: new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000),
    to: today,
  },
];

const formatShort = (d?: Date) => (d ? d.toLocaleDateString() : '—');

function DashboardLayoutInner() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const slot = useDashboardHeaderSlot();

  const projects = useProjects();
  const { pendingJiraCount } = useProjectsMeta();
  const projectsByProvider = useMemo(() => {
    const groups: Record<string, typeof projects> = {};
    for (const p of projects) {
      const provider = p.provider ?? 'jira';
      (groups[provider] ??= []).push(p);
    }
    return groups;
  }, [projects]);

  const [selectedProject, setSelectedProject] = useState<string>(projectId || 'default');

  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>(dateRanges[2]);
  const [isCustomRange, setIsCustomRange] = useState(false);

  const [customRange, setCustomRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: today,
    endDate: today,
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerWrapRef = useRef<HTMLDivElement | null>(null);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    setSelectedProject(next);

    if (next && next !== 'default') {
      const provider = projects.find((p) => p._id === next)?.provider;
      navigate(provider === 'standalone' ? `/standalone/${next}` : `/dashboard/projects/${next}`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'custom') {
      setIsCustomRange(true);
      setShowDatePicker(true);
      return;
    }

    setIsCustomRange(false);
    const rangeId = parseInt(value);
    const range = dateRanges.find((r) => r.id === rangeId);
    if (range) {
      setDateRange({ from: range.from, to: range.to });
    }
  };

  const handleCustomRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setCustomRange({ startDate: start, endDate: end });
    setDateRange({ from: start || undefined, to: end || undefined });
  };

  const activeRangeLabel = useMemo(() => {
    if (!isCustomRange) {
      const found = dateRanges.find(
        (r) =>
          r.from?.toDateString?.() === dateRange.from?.toDateString?.() &&
          r.to?.toDateString?.() === dateRange.to?.toDateString?.(),
      );
      return found?.label || `${formatShort(dateRange.from)} → ${formatShort(dateRange.to)}`;
    }
    return `Custom: ${formatShort(dateRange.from)} → ${formatShort(dateRange.to)}`;
  }, [dateRange.from, dateRange.to, isCustomRange]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!showDatePicker) return;
      const el = datePickerWrapRef.current;
      if (el && !el.contains(e.target as Node)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showDatePicker]);

  // Keep the selector in sync with the route's :projectId — not just the <select>'s own
  // onChange — so navigating in from elsewhere (OtherProjectsCard, a risk-table row, a sidebar
  // link, browser back/forward) updates the dropdown too, not just the page content.
  useEffect(() => {
    setSelectedProject(projectId || 'default');
  }, [projectId]);

  return (
    <div className="min-h-screen bg-base-200">
      {/* Sticky header */}
      <header className="md:sticky md:top-0 z-[1] bg-base-200/80 backdrop-blur border-b border-base-300">
        <div className="px-4 py-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          {/* Title row */}
          <div className="flex items-center justify-between min-w-0">
            <div className="min-w-0">
              <div className="flex flex-row items-center gap-2">
                {slot.breadcrumb && <div>{slot.breadcrumb}</div>}
                <div className="font-display text-lg md:text-2xl font-semibold truncate">
                  {slot.title ?? 'Dashboard'}
                </div>
              </div>
              {slot.subtitle && <div className="text-sm opacity-70 mt-0.5 hidden md:block">{slot.subtitle}</div>}
            </div>
            {/* Ask mFlow — shown inline with title on mobile */}
            <div className="md:hidden shrink-0 ml-2">{slot.actions}</div>
          </div>

          {/* Controls row */}
          <div className="flex flex-row flex-wrap items-center gap-2">
            {/* Ask mFlow — shown in controls row on desktop */}
            <div className="hidden md:block">{slot.actions}</div>

            {/* Active range chip — desktop only */}
            <div className="badge badge-ghost badge-lg self-center hidden md:flex font-mono text-xs">
              {activeRangeLabel}
            </div>

            {/* Project selector */}
            <Select
              onChange={handleSelectChange}
              className="select select-bordered select-sm flex-1 min-w-[130px] md:w-64 md:flex-none bg-base-100"
              value={selectedProject}
            >
              <Select.Option value="default">All Projects</Select.Option>
              {Object.entries(projectsByProvider).map(([provider, group]) => (
                <optgroup key={provider} label={getProviderCapabilities(provider).label}>
                  {group.map((project) => (
                    <Select.Option key={`selector-${project._id}`} value={project._id}>
                      {project.name}
                    </Select.Option>
                  ))}
                </optgroup>
              ))}
            </Select>

            {/* Date range selector */}
            <div className="relative flex-1 min-w-[120px] md:flex-none" ref={datePickerWrapRef}>
              <Select
                onChange={handleDateRangeChange}
                className="select select-bordered select-sm w-full md:w-56 bg-base-100"
                value={isCustomRange ? 'custom' : undefined}
              >
                {dateRanges.map((range) => (
                  <Select.Option key={range.id} value={range.id} selected={range.default}>
                    {range.label}
                  </Select.Option>
                ))}
                <Select.Option value="custom">Custom Range…</Select.Option>
              </Select>

              {isCustomRange && showDatePicker && (
                <div className="absolute right-0 max-md:left-0 max-md:right-auto mt-2 z-50">
                  <div className="card bg-base-100 shadow-panel">
                    <div className="card-body p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold">Select range</div>
                        <button className="btn btn-ghost btn-xs" onClick={() => setShowDatePicker(false)}>
                          Close
                        </button>
                      </div>

                      <DatePicker
                        selected={customRange.startDate}
                        onChange={handleCustomRangeChange}
                        startDate={customRange.startDate}
                        endDate={customRange.endDate}
                        selectsRange
                        inline
                        onCalendarClose={() => setShowDatePicker(false)}
                      />

                      <div className="mt-2 flex items-center justify-between text-xs opacity-70">
                        <span>
                          {formatShort(dateRange.from)} → {formatShort(dateRange.to)}
                        </span>
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => setShowDatePicker(false)}
                          disabled={!dateRange.from || !dateRange.to}
                          title={!dateRange.from || !dateRange.to ? 'Select start and end date' : 'Apply'}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick open datepicker button */}
            {isCustomRange && (
              <button className="btn btn-outline btn-sm" onClick={() => setShowDatePicker((v) => !v)}>
                Dates
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="px-4 py-4">
        {pendingJiraCount > 0 && (
          <div className="alert alert-warning mb-4 text-sm">
            <span>
              {pendingJiraCount} Jira project{pendingJiraCount === 1 ? '' : 's'} still syncing — not shown here yet. Try{' '}
              <b>Sync now</b> on the Integrations page, or check back after the next sync.
            </span>
          </div>
        )}
        <DateRangeProvider value={dateRange}>
          <Outlet />
        </DateRangeProvider>
      </div>
    </div>
  );
}

const DashboardLayout: React.FC = () => (
  <DashboardHeaderProvider>
    <DashboardLayoutInner />
  </DashboardHeaderProvider>
);

export default DashboardLayout;
